import { FC, memo } from 'react'
import { Typography, Box } from '@mui/material'
import type {
  FilterFieldConfig,
  FilterState,
  DateRange,
  FilterOption,
} from '@/utilities/types/shared-filter'
import { UserSelectFilter } from '../UserSelectFilter'
import { UserDateRangeSelector } from './UserDateRangeSelector'
import { TicketSelectFilter } from '../filter-tickets/TicketSelectFilter'

interface FilterFieldProps {
  config: FilterFieldConfig
  filters: FilterState
  onFilterChange: (field: string, value: any) => void
  onDateRangeChange: (params: { field: string; range: DateRange }) => void
  tab?: string
  filterType?: string
}

const isValidDateRange = (
  start: string | null,
  end: string | null,
): boolean => {
  if (!start || !end) return true
  const startDate = new Date(start)
  const endDate = new Date(end)
  return endDate >= startDate
}

export const UserFilterField: FC<FilterFieldProps> = memo(
  ({ config, filters, onFilterChange, onDateRangeChange, tab, filterType }) => {
    const { type, field, label, options } = config

    const renderField = () => {
      switch (type) {
        case 'date':
          return (
            <UserDateRangeSelector
              field={field}
              label={label}
              value={filters.dateRanges[field] || { start: null, end: null }}
              onChange={onDateRangeChange}
              validation={{
                isValidRange: isValidDateRange,
                errorMessage: 'End date cannot be before start date',
              }}
            />
          )
        case 'select':
          if (filterType === 'ticket') {
            return (
              <TicketSelectFilter
                field={field}
                label={label}
                value={filters.regularFilters[field] || []}
                options={options as string[]}
                onChange={onFilterChange}
              />
            )
          } else {
            const mappedOptions:
              | FilterOption[]
              | Record<string, FilterOption[]>
              | undefined = Array.isArray(options)
              ? (options as (string | FilterOption)[]).map((option) => {
                  if (
                    typeof option === 'object' &&
                    option !== null &&
                    'label' in option &&
                    'value' in option
                  ) {
                    return {
                      label:
                        typeof option.label === 'string'
                          ? option.label
                          : String(option.label),
                      value:
                        typeof option.value === 'string'
                          ? option.value
                          : String(option.value),
                    }
                  }
                  return {
                    label: String(option),
                    value: String(option),
                  }
                })
              : options

            return (
              <UserSelectFilter
                field={field}
                label={label}
                value={filters.regularFilters[field] || []}
                options={mappedOptions}
                onChange={onFilterChange}
                isMulti={config.isMulti}
                tab={tab}
              />
            )
          }

        default:
          return null
      }
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
        {renderField()}
      </Box>
    )
  },
)
